
// src/api/authApi.js

import { getErrorCodeByHttpStatus, getErrorMessage } from "../config/errorConfig";
import { LOGIN_URL, LOGOUT_URL } from "../config/apiConfig";
import { deriveCustomerNameFromEmail } from "../utils/auth";

const setAuthCookies = (token, username, fullProfile, role) => {
  const expire = 60 * 60; // 1 hour
  const secureFlags = "path=/; SameSite=Strict; Secure";

  document.cookie = `authToken=${token}; max-age=${expire}; ${secureFlags}`;
  document.cookie = `username=${encodeURIComponent(username)}; max-age=${expire}; ${secureFlags}`;
  document.cookie = `userRole=${role}; max-age=${expire}; ${secureFlags}`; // Store role

  if (fullProfile) {
    const profileValue = encodeURIComponent(JSON.stringify(fullProfile));
    document.cookie = `userProfile=${profileValue}; max-age=${expire}; ${secureFlags}`;
  }
};

export const signIn = async (username, password) => {
  if (!username || !password) {
   throw new Error(getErrorMessage("AUTH_MISSING_FIELDS"));
  }

  // Clear all session storage before login
  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn("Failed to clear session storage", e);
  }

  // Clear all local storage before login
  try {
    localStorage.clear();
  } catch (e) {
    console.warn("Failed to clear local storage", e);
  }

  try {
    // Remove trailing space in URL to avoid issues
    const response = await fetch(
      LOGIN_URL,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      }
    );

    const data = await response.json();
   

    if (!response.ok) {
      const errorCode = getErrorCodeByHttpStatus(response.status);
      throw new Error(getErrorMessage(errorCode));
    }

    let fullProfile, role;

    // Detect admin vs customer response
    if (data.role === "A" && data.users) {
      // Admin response
      role = "A";
      fullProfile = {
        admin_username: data.admin_username,
        role: "A",
        token: data.token,
        users: data.users, // store full user list for admin panel
      };
    } else if (data.role === "C" && data.customer_id) {
      // Customer response
      role = "C";
      fullProfile = {
        customer_id: data.customer_id,
        customer_name: deriveCustomerNameFromEmail(
          data.username || data.email,
          data.customer_name
        ),
        username: data.username,
        role: "C",
        token: data.token,
      };
    } else {
        console.error("Unexpected server response:", data);
      throw new Error(getErrorMessage("API_BAD_REQUEST"));
    }

    // Save to cookies (including role)
    setAuthCookies(data.token, data.username || data.admin_username, fullProfile, role);
    sessionStorage.setItem("CallAvatarVideo", true);

    return fullProfile;
  } catch (error) {
    if (error.message.includes("Failed to fetch")) {
      throw new Error(getErrorMessage("NETWORK_SERVER_UNREACHABLE"));
    }
    throw error;
  }
};
// Placeholder for future use
export const signUp = async () => {
  throw new Error("Sign up is not supported. Please use valid credentials.");
};

// Logout API call
export const logout = async () => {
  const token = document.cookie.replace(
    /(?:(?:^|.*;\s*)authToken\s*=\s*([^;]*).*$)|^.*$/,
    "$1"
  );

  if (!token) {
    // No token means already logged out
    return { success: true, message: "Already logged out" };
  }

  try {
    const response = await fetch(
      LOGOUT_URL,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      }
    );

    // Even if API fails, we still want to logout locally
    const data = await response.json().catch(() => ({}));
    
    return {
      success: response.ok || response.status === 401 || response.status === 404,
      message: data.message || "Logged out successfully",
    };
  } catch (error) {
    // Network error - still allow logout locally
    console.warn("Logout API error (continuing with local logout):", error);
    return {
      success: true,
      message: "Logged out successfully",
    };
  }
};

// Validates a Firebase ID token with the backend and sets auth cookies.
export const validateUser = async (token) => {
  const res = await fetch(
    "https://orbiee-backend-dev-218142584790.us-central1.run.app/auth_validate",
    {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  const data = await res.json();
  if (!res.ok) throw new Error("Validation failed");

  let fullProfile, role;

  if (data.role === "A" && data.users) {
    role = "A";
    fullProfile = {
      admin_username: data.admin_username,
      role: "A",
      token,
      users: data.users,
    };
  } else if (data.role === "C" && data.user_id) {
    role = "C";
    const customerEmail = data.email || data.username;
    fullProfile = {
      customer_id: data.user_id,
      customer_name: deriveCustomerNameFromEmail(
        customerEmail,
        data.customer_name
      ),
      username: customerEmail,
      role: "C",
      token,
    };
  } else {
    console.error("Unexpected server response:", data);
    throw new Error(getErrorMessage("API_BAD_REQUEST"));
  }

  setAuthCookies(token, data.username || data.admin_username, fullProfile, role);
  sessionStorage.setItem("CallAvatarVideo", true);

  return data;
};





// const API_URL = "https://us-central1-retail-jkommerce-poc.cloudfunctions.net/login_v1";

// const setAuthCookies = (token, username, role) => {
//   const expire = 60 * 60; // 1 hour
//   const secureFlags = "path=/; SameSite=Strict; Secure";

//   document.cookie = `authToken=${token}; max-age=${expire}; ${secureFlags}`;
//   document.cookie = `username=${encodeURIComponent(username)}; max-age=${expire}; ${secureFlags}`;
//   document.cookie = `userRole=${role}; max-age=${expire}; ${secureFlags}`;
//   // DO NOT store userProfile with users array in cookie
// };

// export const signIn = async (username, password) => {
//   if (!username || !password) {
//     throw new Error("Username and password are required");
//   }

//   try {
//     const response = await fetch(API_URL, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ username, password }),
//     });

//     const data = await response.json();

//     if (!response.ok) {
//       throw new Error(data.error || data.message || "Invalid credentials");
//     }

//     let fullProfile, role;

//     if (data.role === "A" && data.users) {
//       role = "A";
//       fullProfile = {
//         admin_username: data.admin_username,
//         role: "A",
//         token: data.token,
//         users: data.users, // Keep in memory, not cookie
//       };
//     } else if (data.role === "C" && data.customer_id) {
//       role = "C";
//       fullProfile = {
//         customer_id: data.customer_id,
//         customer_name: data.customer_name,
//         username: data.username,
//         role: "C",
//         token: data.token,
//       };
//     } else {
//       throw new Error("Unexpected response format from server");
//     }

//     // Only store minimal auth data in cookies
//     setAuthCookies(data.token, data.username || data.admin_username, role);

//     return fullProfile; // Return full profile (including users for admin)
//   } catch (error) {
//     if (error.message.includes("Failed to fetch")) {
//       throw new Error(
//         "Network error: Unable to connect to server. Check your internet or backend."
//       );
//     }
//     throw error;
//   }
// };

// export const signUp = async () => {
//   throw new Error("Sign up is not supported. Please use valid credentials.");
// };


// const API_URL = "https://us-central1-retail-jkommerce-poc.cloudfunctions.net/login_details";

// // Helper to set auth cookies (called after successful login)
// const setAuthCookies = (token, username, fullProfile) => {
//   const expire = 60 * 60; // 1 hour
//   const secureFlags = "path=/; SameSite=Strict; Secure";

//   // Auth token (for API calls)
//   document.cookie = `authToken=${token}; max-age=${expire}; ${secureFlags}`;

//   // Username (for quick display)
//   document.cookie = `username=${encodeURIComponent(username)}; max-age=${expire}; ${secureFlags}`;

//   // Full user profile (for rich UI: name, customer ID, etc.)
//   if (fullProfile) {
//     const profileValue = encodeURIComponent(JSON.stringify(fullProfile));
//     document.cookie = `userProfile=${profileValue}; max-age=${expire}; ${secureFlags}`;
//   }
// };

// // Sign in function
// export const signIn = async (username, password) => {
//   if (!username || !password) {
//     throw new Error("Username and password are required");
//   }

//   try {
//     const response = await fetch(API_URL, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ username, password }),
//     });

//     const data = await response.json();

//     if (!response.ok) {
//       throw new Error(data.error || data.message || "Invalid credentials");
//     }

//     if (!data.token) {
//       throw new Error("No token received from server");
//     }

//     // Construct full profile from API response
//     const fullProfile = {
//       customer_id: data.customer_id,
//       customer_name: data.customer_name,
//       username: data.username,
//       token: data.token,
//     };

//     // Save all auth data to cookies
//     setAuthCookies(data.token, data.username, fullProfile);

//     return fullProfile;
//   } catch (error) {
//     if (error.message.includes("Failed to fetch")) {
//       throw new Error(
//         "Network error: Unable to connect to server. Check your internet or backend."
//       );
//     }
//     throw error;
//   }
// };

// // Placeholder for future use
// export const signUp = async () => {
//   throw new Error("Sign up is not supported. Please use valid credentials.");
// };